import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import * as clientesApi from '../../api/clientes';
import * as modelosApi from '../../api/modelos';
import * as demandasApi from '../../api/demandas';
import * as pedidosApi from '../../api/pedidos';
import * as tamanhosApi from '../../api/tamanhos';
import { formatDate } from '../../utils/date';
import { formatMoeda } from '../../utils/fmt';

function StatCard({ valor, label, cor, to }) {
  const content = (
    <div className="cjc-stat-card h-100">
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ color: cor || 'var(--cjc-primary)' }}>{valor ?? '—'}</div>
    </div>
  );
  return to ? <Link to={to} style={{ textDecoration: 'none' }}>{content}</Link> : content;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [pedidosRecentes, setPedidosRecentes] = useState([]);
  const [demandasAbertas, setDemandasAbertas] = useState([]);
  const [topClientes, setTopClientes] = useState([]);
  const [clientesMap, setClientesMap] = useState({});
  const [modelosMap, setModelosMap] = useState({});
  const [tamanhosMap, setTamanhosMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [erroCarregar, setErroCarregar] = useState('');

  useEffect(() => {
    Promise.all([
      clientesApi.listar(),
      modelosApi.listar(),
      demandasApi.listar({ status: 'aberta' }),
      demandasApi.listar({ status: 'parcialmente_atendida' }),
      pedidosApi.listar(),
      tamanhosApi.listar(),
    ]).then(([cli, mod, dem1, dem2, ped, tam]) => {
      const cMap = Object.fromEntries(cli.data.map(c => [c.id_cliente_final, c.nome]));
      const mMap = Object.fromEntries(mod.data.map(m => [m.id_modelo_uniforme, m.nome]));
      const tMap = Object.fromEntries(tam.data.map(t => [t.id_tamanho_uniforme, t.nome]));
      setClientesMap(cMap);
      setModelosMap(mMap);
      setTamanhosMap(tMap);

      setStats({
        clientes: cli.data.length,
        modelos: mod.data.length,
        demandasAbertas: dem1.data.length + dem2.data.length,
        pedidos: ped.data.length,
      });
      setPedidosRecentes(ped.data.slice(0, 5));
      setDemandasAbertas([...dem1.data, ...dem2.data].slice(0, 5));

      // Ranking de clientes por nº de pedidos + valor total — visão da empresa como um
      // todo, útil pra operador que atende vários clientes na mesma plataforma.
      const porCliente = {};
      ped.data.forEach(p => {
        const key = p.id_cliente_final;
        if (!porCliente[key]) porCliente[key] = { id: key, pedidos: 0, valor: 0 };
        porCliente[key].pedidos += 1;
        porCliente[key].valor += Number(p.valor_total) || 0;
      });
      setTopClientes(Object.values(porCliente).sort((a, b) => b.valor - a.valor).slice(0, 5));
    }).catch(() => setErroCarregar('Erro ao carregar dados do dashboard.'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <PageHeader
        titulo="Dashboard"
        subtitulo={`Olá, ${user?.nome || user?.email}`}
      />

      {erroCarregar && <div className="alert alert-danger py-2">{erroCarregar}</div>}

      <div className="row g-3 mb-4">
        <div className="col-6 col-lg-3">
          <StatCard valor={stats.clientes} label="Clientes" to="/clientes" />
        </div>
        <div className="col-6 col-lg-3">
          <StatCard valor={stats.modelos} label="Modelos" to="/modelos" />
        </div>
        <div className="col-6 col-lg-3">
          <StatCard valor={stats.demandasAbertas} label="Demandas em Aberto" cor="var(--cjc-accent)" to="/demandas" />
        </div>
        <div className="col-6 col-lg-3">
          <StatCard valor={stats.pedidos} label="Pedidos" to="/pedidos" />
        </div>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-lg-6">
          <div className="card h-100">
            <div className="card-header">Pedidos Recentes</div>
            <div className="card-body p-0">
              {loading ? (
                <div className="p-3 text-muted">Carregando...</div>
              ) : pedidosRecentes.length === 0 ? (
                <div className="p-3 text-muted">Nenhum pedido ainda.</div>
              ) : (
                <table className="table table-hover mb-0">
                  <thead>
                    <tr>
                      <th>Número</th><th>Cliente</th><th>Total</th><th>Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pedidosRecentes.map(p => (
                      <tr key={p.id_pedido}>
                        <td><Link to={`/pedidos/${p.id_pedido}`}>{p.numero_pedido}</Link></td>
                        <td style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {clientesMap[p.id_cliente_final] || p.id_cliente_final}
                        </td>
                        <td>{formatMoeda(p.valor_total)}</td>
                        <td>{formatDate(p.data_pedido)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        <div className="col-lg-6">
          <div className="card h-100">
            <div className="card-header" style={{ color: 'var(--cjc-accent)' }}>
              Demandas em Aberto
            </div>
            <div className="card-body p-0">
              {loading ? (
                <div className="p-3 text-muted">Carregando...</div>
              ) : demandasAbertas.length === 0 ? (
                <div className="p-3 text-muted">Nenhuma demanda em aberto.</div>
              ) : (
                <table className="table table-hover mb-0">
                  <thead>
                    <tr>
                      <th>Modelo</th><th>Tam.</th><th>Qtd Pendente</th><th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {demandasAbertas.map(d => (
                      <tr key={d.id_demanda}>
                        <td style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {modelosMap[d.id_modelo_uniforme] || d.id_modelo_uniforme}
                        </td>
                        <td>{tamanhosMap[d.id_tamanho_uniforme] || d.id_tamanho_uniforme}</td>
                        <td>{d.quantidade_pendente}</td>
                        <td>
                          <span className={`badge ${d.status === 'aberta' ? 'bg-warning text-dark' : 'bg-info text-dark'}`}>
                            {d.status === 'aberta' ? 'Aberta' : 'Parcial'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3">
        <div className="col-lg-6">
          <div className="card">
            <div className="card-header">Top Clientes (por valor)</div>
            <div className="card-body p-0">
              {loading ? (
                <div className="p-3 text-muted">Carregando...</div>
              ) : topClientes.length === 0 ? (
                <div className="p-3 text-muted">Nenhum pedido ainda.</div>
              ) : (
                <table className="table table-hover mb-0">
                  <thead>
                    <tr><th>Cliente</th><th>Pedidos</th><th>Valor Total</th></tr>
                  </thead>
                  <tbody>
                    {topClientes.map(c => (
                      <tr key={c.id}>
                        <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {clientesMap[c.id] || c.id}
                        </td>
                        <td>{c.pedidos}</td>
                        <td className="fw-semibold">{formatMoeda(c.valor)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
